import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions, NOMBRES, seededRandom } from "../spec";

const SHEET = "LG Validación";
const TABLE = "BaseClientes";

const rnd = seededRandom(4404);

const correoDe = (nombre: string) =>
  nombre
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(" ", ".");

// ID, Nombre, Teléfono, Correo, Límite crédito, Compras, Pedidos, Ticket (fórmula)
const ROWS = NOMBRES.slice(0, 32).map((nombre, i) => {
  const usuario = correoDe(nombre);
  const nombreCapturado: CellValue = i === 6 || i === 19 ? 40000 + i : nombre;
  // A leading apostrophe keeps number-looking text as text when written.
  const telefono: CellValue =
    i % 7 === 3 ? `'55-${rnd.int(1000, 9999)}-${rnd.int(1000, 9999)}` : i === 24 ? "Sin teléfono" : rnd.int(5510000000, 5599999999);
  const correo =
    i % 6 === 2 ? "" : i % 9 === 5 ? `${usuario}.gmail.com` : `${usuario}@correo.mx`;
  const limite: CellValue = i % 8 === 4 ? "N/D" : i === 15 ? "'15,000" : rnd.round(5000, 80000, 1000);
  const compras = rnd.round(2000, 90000, 100);
  const pedidos = i % 10 === 7 ? 0 : rnd.int(1, 40);
  return [`C-${1001 + i}`, nombreCapturado, telefono, correo, limite, compras, pedidos, "=[@Compras]/[@Pedidos]"];
});

type Row = (typeof ROWS)[number];
const sinCorreo = (r: Row) => r[3] === "";
const telNumerico = (r: Row) => typeof r[2] === "number";
const ticketError = (r: Row) => r[6] === 0;

export const spec: FuncionProjectSpec = {
  projectId: "funciones-es-validacion",
  sheets: [
    {
      name: SHEET,
      title: "Limpieza de la base de clientes",
      subtitle: "Base exportada del CRM — encuentra lo que está mal antes de cargarla al nuevo sistema",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["ID", "Nombre", "Teléfono", "Correo", "Límite crédito", "Compras", "Pedidos", "Ticket"],
          rows: ROWS,
          answerColumns: [
            "Teléfono numérico",
            "Sin correo",
            "Nombre válido",
            "Error en ticket",
            "Correo válido",
            "Límite",
            "Estatus",
          ],
          formats: { Teléfono: "0", Compras: "$#,##0", Ticket: "$#,##0.00" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Teléfono numérico", fns: ["ISNUMBER"],
      expected: ROWS.map(telNumerico),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Sin correo", fns: ["ISBLANK"],
      expected: ROWS.map(sinCorreo),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nombre válido", fns: ["ISTEXT"],
      expected: ROWS.map((r) => typeof r[1] === "string"),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Error en ticket", fns: ["ISERROR"],
      expected: ROWS.map(ticketError),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Correo válido", fns: ["ISNUMBER", "SEARCH"],
      expected: ROWS.map((r) => String(r[3]).includes("@")),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Límite", fns: ["IF", "ISNUMBER"],
      expected: ROWS.map((r) => (typeof r[4] === "number" ? "OK" : "Corregir")),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Estatus", fns: ["IF", "OR", "ISBLANK", "ISNUMBER", "ISERROR"],
      expected: ROWS.map((r) =>
        sinCorreo(r) || !telNumerico(r) || ticketError(r) ? "Incompleto" : "Completo"
      ),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-es-validacion",
  title: "ESNUMERO, ESTEXTO, ESBLANCO, ESERROR: Limpieza de datos",
  subtitle: "Revisa el tipo de dato antes de calcular",
  description:
    "Te pasaron la base de clientes exportada del CRM para cargarla a un sistema nuevo. Hay teléfonos capturados como texto, correos vacíos o sin @, nombres que en realidad son números y límites de crédito escritos como \"N/D\".\n\nLas funciones ES… responden VERDADERO o FALSO sobre el tipo de dato de una celda. Con ellas marcarás cada problema antes de que llegue al sistema nuevo.",
  topics: [
    "ESNUMERO, ESTEXTO, ESBLANCO y ESERROR",
    "Buscar un carácter con ESNUMERO(HALLAR(...))",
    "Funciones ES… dentro de SI",
    "Varias validaciones en una sola regla",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: ¿El teléfono es un número?",
      description:
        "En la hoja <strong>LG Validación</strong>, columna <strong>Teléfono numérico</strong>: usa <strong>ESNUMERO</strong> para saber si el <strong>Teléfono</strong> se guardó como número (VERDADERO) o como texto, por ejemplo con guiones (FALSO).",
      solution: [
        `Selecciona ${F("I5")}.`,
        `Escribe ${F("=ESNUMERO([@Teléfono])")}.`,
        "Fíjate en las filas con FALSO: el teléfono tiene guiones o letras, así que Excel lo guardó como texto.",
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=ESNUMERO([@Teléfono])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Correos vacíos",
      description:
        "En la columna <strong>Sin correo</strong>: usa <strong>ESBLANCO</strong> para marcar con VERDADERO los clientes cuyo <strong>Correo</strong> está vacío.",
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F("=ESBLANCO([@Correo])")}.`,
        "Ojo: ESBLANCO devuelve FALSO si la celda tiene una fórmula, aunque se vea vacía.",
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=ESBLANCO([@Correo])",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Nombres mal capturados",
      description:
        "En la columna <strong>Nombre válido</strong>: usa <strong>ESTEXTO</strong>. Algunos nombres se capturaron como números por error y deben quedar en FALSO.",
      solution: [
        `Selecciona ${F("K5")}.`,
        `Escribe ${F("=ESTEXTO([@Nombre])")}.`,
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=ESTEXTO([@Nombre])",
    },
    {
      id: 4,
      level: "basico",
      title: "Tarea 4: Tickets con error",
      description:
        "La columna <strong>Ticket</strong> divide Compras entre Pedidos, y algunos clientes tienen 0 pedidos. En la columna <strong>Error en ticket</strong>, usa <strong>ESERROR</strong> para marcar con VERDADERO los tickets que muestran un error.",
      solution: [
        `Selecciona ${F("L5")}.`,
        `Escribe ${F("=ESERROR([@Ticket])")}.`,
        "ESERROR detecta cualquier tipo de error: #DIV/0!, #N/D, #VALOR!, etc.",
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: "=ESERROR([@Ticket])",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿El correo tiene @?",
      description:
        "En la columna <strong>Correo válido</strong>: devuelve VERDADERO si el correo contiene el carácter <strong>@</strong>. Combina <strong>HALLAR</strong> (que devuelve la posición del carácter o un error si no está) con <strong>ESNUMERO</strong>.",
      solution: [
        `Selecciona ${F("M5")}.`,
        `${F('HALLAR("@",[@Correo])')} devuelve un número si encuentra la @ y #¡VALOR! si no la encuentra.`,
        `ESNUMERO convierte eso en VERDADERO o FALSO: ${F('=ESNUMERO(HALLAR("@",[@Correo]))')}.`,
      ],
      expectedResult: "VERDADERO / FALSO",
      finalFormula: '=ESNUMERO(HALLAR("@",[@Correo]))',
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Límite de crédito",
      description:
        "En la columna <strong>Límite</strong>: escribe <strong>OK</strong> si el <strong>Límite crédito</strong> es un número; si no (por ejemplo \"N/D\" o \"15,000\" escrito como texto), escribe <strong>Corregir</strong>.",
      solution: [
        `Selecciona ${F("N5")}.`,
        "Las funciones ES… devuelven VERDADERO o FALSO, así que funcionan directamente como prueba lógica de SI.",
        `Escribe ${F('=SI(ESNUMERO([@[Límite crédito]]),"OK","Corregir")')}.`,
      ],
      expectedResult: "OK / Corregir",
      finalFormula: '=SI(ESNUMERO([@[Límite crédito]]),"OK","Corregir")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Estatus del registro",
      description:
        conditions(
        "En la columna <strong>Estatus</strong>, el registro está <strong>Incompleto</strong> si pasa <strong>cualquiera</strong> de estas cosas:",
        [
          "Le falta el <strong>correo</strong>",
          "El <strong>teléfono</strong> no es numérico",
          "Su <strong>ticket</strong> tiene error",
        ],
        "En cualquier otro caso está <strong>Completo</strong>."
      ),
      solution: [
        `Selecciona ${F("O5")}.`,
        `Junta las tres revisiones dentro de un ${F("O(...)")}. Para \"no es numérico\" usa ${F("NO(ESNUMERO(...))")}.`,
        `Escribe ${F('=SI(O(ESBLANCO([@Correo]),NO(ESNUMERO([@Teléfono])),ESERROR([@Ticket])),"Incompleto","Completo")')}.`,
      ],
      expectedResult: "Incompleto / Completo",
      finalFormula:
        '=SI(O(ESBLANCO([@Correo]),NO(ESNUMERO([@Teléfono])),ESERROR([@Ticket])),"Incompleto","Completo")',
    },
  ],
};
